import type { SupportedLanguage } from './storage';

/**
 * Plural forms live inside one translation value, separated by `|`, in the
 * CLDR order of the locale: English `one|other`, Arabic
 * `zero|one|two|few|many|other`. `translate` picks the form when the params
 * carry a numeric `count`, so call sites stay `t('common.cards', { count })`.
 */
export const PLURAL_SEPARATOR = '|';

const FORMS_BY_LANGUAGE: Record<
  SupportedLanguage,
  readonly Intl.LDMLPluralRule[]
> = {
  en: ['one', 'other'],
  ar: ['zero', 'one', 'two', 'few', 'many', 'other'],
};

/** The forms a locale's plural values must provide, in order. */
export function pluralFormsFor(
  language: SupportedLanguage
): readonly Intl.LDMLPluralRule[] {
  return FORMS_BY_LANGUAGE[language] ?? FORMS_BY_LANGUAGE.en;
}

export function isPluralValue(value: string): boolean {
  return value.includes(PLURAL_SEPARATOR);
}

function languageOfForms(count: number): SupportedLanguage | null {
  return (
    (Object.keys(FORMS_BY_LANGUAGE) as SupportedLanguage[]).find(
      language => FORMS_BY_LANGUAGE[language].length === count
    ) ?? null
  );
}

/**
 * Picks the form for `count` from a `|`-separated value. The form set (two or
 * six) decides the rules, so an English fallback shown in an Arabic UI is still
 * read as `one|other`. Anything else falls back to the last form (`other`).
 */
export function plural(value: string, count: number): string {
  const forms = value.split(PLURAL_SEPARATOR).map(form => form.trim());
  const language = languageOfForms(forms.length);
  if (!language) return forms[forms.length - 1];
  const category = new Intl.PluralRules(language).select(count);
  const index = FORMS_BY_LANGUAGE[language].indexOf(category);
  return forms[index >= 0 ? index : forms.length - 1];
}
